import React, { useState, useEffect, useRef } from 'react'
import useElementDimensions from '../../../hooks/useElementDimensions'
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect'
import { DEFAULT_LARGE_TRANSITION_DURATION_MS } from '../../../constants/statics'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const TRANSITION_DELAY_MS = 30

interface HorizontalExpanderProps {
  children: React.ReactNode
  maxWidth?: number
  transitionDurationMs?: number
}

/**
 * HorizontalExpander Component
 * Expands horizontally with a smooth transition effect based on content width changes.
 * It adjusts its width dynamically and supports a maximum width limit.
 */
const HorizontalExpander: React.FC<HorizontalExpanderProps> = ({
  children,
  maxWidth,
  transitionDurationMs = DEFAULT_LARGE_TRANSITION_DURATION_MS as number,
}) => {
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [prevWidth, setPrevWidth] = useState<number>(0)
  const [animating, setAnimating] = useState<boolean>(false)
  const content = useRef<HTMLDivElement>(null)
  const rect = useElementDimensions(content)

  // track prior width
  useEffect(() => {
    if (rect?.width && rect.width !== prevWidth && !animating) {
      setPrevWidth(rect.width)
    }
  }, [rect?.width, animating, prevWidth])

  const clearTimers = () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = null
    }
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current)
      cleanupTimeoutRef.current = null
    }
  }

  // clear pending timers on unmount
  useEffect(() => clearTimers, [])

  // Set the width to the previous width and then resets it to the current width
  useIsomorphicLayoutEffect(() => {
    if (content.current && !animating) {
      const currentWidth = rect?.width || 0

      // Skip animation when measured width is 0 (hidden/collapsed element).
      // Pinning an inline width of 0px makes the observer keep reporting 0,
      // which deadlocks the expander at zero width. Reset to the stylesheet
      // fit-content so it recovers once visible again.
      if (currentWidth === 0) {
        if (prevWidth !== 0) {
          clearTimers()
          content.current.style.width = ''
          setPrevWidth(0)
        }
        return
      }

      if (currentWidth !== prevWidth && !animating) {
        setAnimating(true)

        // Clear any existing timeouts to prevent conflicts
        clearTimers()

        // Set the width to the previous width first
        content.current.style.width = `${prevWidth || rect?.width}px`
        animationTimeoutRef.current = setTimeout(
          () => {
            if (content.current) {
              content.current.style.width = `${currentWidth}px`
            }
            cleanupTimeoutRef.current = setTimeout(() => {
              if (content.current) {
                // content.current.style.width = 'fit-content'
                if (!maxWidth || currentWidth < maxWidth) {
                  content.current.style.width = 'fit-content'
                } else {
                  content.current.style.width = ''
                }
              }
              setAnimating(false)
            }, transitionDurationMs + TRANSITION_DELAY_MS)
          },
          TRANSITION_DELAY_MS // slight delay to allow for transition effect
        )
      }
    }
  }, [rect?.width, prevWidth, animating])

  return (
    <div className={cx('horizontal-expander')}>
      <div
        ref={content}
        className={cx('horizontal-expander-content')}
        style={{
          maxWidth: maxWidth ? `${maxWidth}px` : 'none',
          transition: `width ${transitionDurationMs}ms ease-in-out`,
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default HorizontalExpander
